import type { IQuote, SessionInfo } from '@mindverse/accessor-open';
import { useCallback, useContext, useEffect, useState } from 'react';

import ContextStorage from '../context/Storage';
import type { SendMsgData } from './useChatSession';

export enum EStorageKey {
  SESSION_ID = 'SESSION_ID',
  MESSAGE_ID = 'MESSAGE_ID',
  ANSWER_ING = 'ANSWER_ING',
  ANSWER_STOPPED = 'ANSWER_STOPPED',
  GREETING_READIED = 'GREETING_READIED',
  SEND_MSG_LOADING = 'SEND_MSG_LOADING',
  WAIT_TO_SEND = 'WAIT_TO_SEND',
  FIRST_SEND_MSG = 'FIRST_SEND_MSG',
  SHOW_CONTINUE = 'CONTINUE',
  MIND_ID = 'MIND_ID',
  READY_TO_SET_CHAT_LIST = 'READY_TO_SET_CHAT_LIST',
  SESSION_INFO = 'SESSION_INFO',
  CONNECTED_SESSION_ID_LIST = 'CONNECTED_SESSION_ID_LIST',
  GREETING_ONLY = 'GREETING_ONLY',
  SPARK = 'SPARK',
  ANONYMOUS_INIT_LOADING = 'ANONYMOUS_INIT_LOADING',
  ICE_BREAKING = 'ICE_BREAKING',
  SHOW_MESSAGE_LOADING = 'SHOW_MESSAGE_LOADING',
  INPUT_SHOW_LOGIN = 'INPUT_SHOW_LOGIN',
  INPUT_SHOW_LOGIN_MODAL = 'INPUT_SHOW_LOGIN_MODAL',
  QUOTE = 'QUOTE'
}

export interface IStorageValueMapping {
  [EStorageKey.SESSION_ID]: string;
  [EStorageKey.MIND_ID]: string;
  [EStorageKey.MESSAGE_ID]: string; // 当前messageId
  [EStorageKey.ANSWER_ING]: boolean; // 是否正在回答中
  [EStorageKey.ANSWER_STOPPED]: boolean; // 是否在暂停回答中
  [EStorageKey.GREETING_READIED]: boolean; // 开场白已出现
  [EStorageKey.SEND_MSG_LOADING]: boolean; // 发送消息 Loading
  [EStorageKey.WAIT_TO_SEND]: boolean; // 等待发送消息
  [EStorageKey.FIRST_SEND_MSG]: SendMsgData | undefined; // session刚创建时第一次发送的消息
  [EStorageKey.READY_TO_SET_CHAT_LIST]: boolean; // 是否准备好设置 chat list
  [EStorageKey.SESSION_INFO]: SessionInfo | null | undefined; // session info
  [EStorageKey.CONNECTED_SESSION_ID_LIST]: string[]; // 已连接的 session id 列表
  [EStorageKey.GREETING_ONLY]: boolean; // 消息只有开场白
  [EStorageKey.ICE_BREAKING]: boolean; // 是否正在破冰
  [EStorageKey.ANONYMOUS_INIT_LOADING]: boolean; // 是否正在创建匿名账户
  [EStorageKey.SHOW_MESSAGE_LOADING]: boolean; // 是否可以展示loading，具体展示与否还要取决于业务逻辑
  [EStorageKey.INPUT_SHOW_LOGIN]: boolean; // 输入框变成登录按钮
  [EStorageKey.QUOTE]: IQuote | undefined; // 手动引用内容
}

type Updater<V> = V | ((prev: V | undefined) => V);

function useMindStorage<K extends keyof IStorageValueMapping>(
  key: K
): [IStorageValueMapping[K] | undefined, (newValue: Updater<IStorageValueMapping[K]>) => void] {
  const storage = useContext(ContextStorage);
  const [value, setValue] = useState<IStorageValueMapping[K] | undefined>(() => storage.get(key));

  const setStorage = useCallback(
    (newValue: Updater<IStorageValueMapping[K]>) => {
      const prev = storage.get(key) as IStorageValueMapping[K] | undefined;

      const next =
        typeof newValue === 'function'
          ? (newValue as (p: typeof prev) => typeof prev)(prev)
          : newValue;

      // 避免不必要的更新
      if (Object.is(prev, next)) return;

      storage.emit(key, next);
    },
    [storage, key]
  );

  useEffect(() => {
    const currentValue = storage.get(key);

    const handleChange = (v: IStorageValueMapping[K]) => {
      setValue(v);
    };

    storage.on(key, handleChange);

    if (!Object.is(value, currentValue)) {
      setValue(currentValue);
    }

    return () => {
      storage.off(key, handleChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage, key]); // 这里也别忘了依赖

  return [value, setStorage];
}

export default useMindStorage;
